import { WindowActionsBar } from '@progress/kendo-react-dialogs'
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators'
import { Drawer, DrawerContent, DrawerItem, DrawerItemProps, DrawerSelectEvent } from '@progress/kendo-react-layout'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AssetApi } from '../../redux/api/AssetApi'
import { Api } from '../../redux/api/BaseApi'
import { TicketApi } from '../../redux/api/TicketApi'
import { TicketDrawerProps } from '../../types/Props/TicketDrawerProps'
import LogOptions from './FormControls/LogOptions'
import TicketHierarchy from './FormControls/TicketHierarchy'
import ExistingTickets from './GridControls/ExistingTickets'
import TicketAssets from './GridControls/TicketAssets'
import TicketDetails from './TicketDetails/NewTicketDetails'
import { Button } from "@progress/kendo-react-buttons";
import { useReduxDispatch, useReduxSelector } from '../../redux/hooks'
import Templates from './FormControls/Templates'
import * as TicketSlice from '../../slices/TicketSlice'
//import { UserApi } from '../../redux/api/UserApi'
import { IStaff } from '../../types/IStaff'
import KnowledgeBase from './FormControls/KnowledgeBase'
import Attachments from './FormControls/Attachments'
import { FileType } from '../../types/File/FileType'
import { FileApi } from '../../redux/api/FileApi'
import * as KBSlice from '../../slices/KBSlice'
import { KBApi } from '../../redux/api/KBApi'
import { KBSearchProps } from '../../types/KB/KBSearchProps'
import SplitActions from './FormControls/SplitActions'
import { IKBSearchResult } from '../../types/KB/IKBSearchResult'
import { useCurrentUserContext } from '../../context/UserContext'


const TicketDrawer: FunctionComponent<TicketDrawerProps> = props => {
  const { profile: userProfile } = useCurrentUserContext();
  const { ticket, splitFromTicketOptions } = useReduxSelector(state => state.newTicket);
  const { data: apifiles } = FileApi.useGetFilesQuery({ typeId: FileType.Call_Action_Internal, primaryId: ticket?.ID | 0 });
  const { data: isFileActive } = FileApi.useIsActiveQuery(FileType.Call_Action_Internal)
  const { data: assets } = AssetApi.useGetAssetsQuery({ contactId: 0, locationId: ticket.Company_Address_Id }, { skip: !ticket.Company_Address_Id })
  const contactAssets = assets?.filter((a) => a.Contact_Id === ticket.Company_Contact)
  const { data: existingTickets } = TicketApi.useGetOpenEndUserTicketsQuery(ticket.Company_Contact_Uid, { skip: !ticket.Company_Contact_Uid })
  const { data: clientTickets } = TicketApi.useGetOpenTicketsQuery({ companyId: ticket.Company_Id }, { skip: !ticket.Company_Id })
  const { data: templateActions } = TicketApi.useGetTemplateActionsQuery(ticket.Template_Id, { skip: !ticket.Template_Id })
  const { data: templates } = TicketApi.useGetMyTemplatesQuery();
  const { data: template } = TicketApi.useGetTemplateQuery(ticket.Template_Id, { skip: !ticket.Template_Id });
  const { data: status } = TicketApi.useGetStatusQuery(template?.Status_Id, { skip: !template?.Status_Id });
  const { results: kbResults } = useReduxSelector(state => state.kb);
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  function isSelected(route) {
    return location.pathname.indexOf(route) > -1;
  }
  function isDetailsPage() {
    //check for /ticket/details OR just /ticket
    return location.pathname.endsWith('/ticket/new') || location.pathname.endsWith('/ticket/new/') || location.pathname.endsWith('/ticket/new/details');
  }

  const items = [
    {
      text: `Templates`, route: "/ticket/new/templates", selected: isSelected('templates'), icon: "k-i-copy",
      badge: `${ticket.Template_Id ? templateActions?.length || '0' : ''}`,
      title: ticket.Template_Id ? `${templateActions?.length || '0'} Template ${userProfile.Phrases.Action}${templateActions?.length === 1 ? '' : 's'}` : null,
      visible: templates?.length ? 'true' : null
    },
    {
      separator: true,
      visible: templates?.length ? 'true' : null
    },
    {
      text: `${userProfile?.Phrases.CallPhrase} Details`,
      route: "/ticket/new/details", selected: isDetailsPage(), icon: "k-i-edit", visible: 'true'
    },
    { separator: true, visible: 'true' },
    {
      title: "Attachments",
      text: "Attachments",
      route: "/ticket/new/file",
      selected: isSelected('file'),
      icon: "k-i-clip",
      visible: isFileActive ? 'true' : null,
      badge: `${apifiles?.length || ''}`
    },
    {
      separator: true,
      visible: isFileActive ? 'true' : null,
    },
    {
      text: ticket.Is_Parent ? "Parent" : ticket.Parent_Call_Id > 0 ? `Child to ${ticket.Parent_Call_Id}` : "Parent / Child",
      route: "/ticket/new/hierarchy",
      icon: "k-i-link-horizontal",
      selected: isSelected('hierarchy'),
      visible: 'true'
    },

    { separator: true, visible: 'true' },
    {
      text: `Open ${userProfile?.Phrases.CallPhrase}s`,
      route: "/ticket/new/existing",
      icon: "k-i-calendar",
      badge: `${existingTickets?.length || 0} / ${clientTickets?.length || 0}`,
      disabled: (!ticket.Company_Contact_Uid),
      selected: isSelected('existing'),
      visible: 'true'
    },
    { separator: true, visible: userProfile?.Features.View_Assets && userProfile?.Features.Asset_Manager ? 'true' : null },
    {
      text: `${userProfile?.Phrases.Asset}s ${ticket?.Assets?.length > 0 ? '(' + ticket?.Assets?.length + ')' : ''}`,
      route: "/ticket/new/assets",
      selected: isSelected('assets'),
      badge: `${contactAssets?.length || 0} / ${assets?.length || 0}`,
      disabled: (!ticket.Company_Contact_Uid),
      icon: "k-i-toggle-full-screen-mode",
      visible: userProfile?.Features.View_Assets && userProfile?.Features.Asset_Manager ? 'true' : null
    },
    {
      separator: true,
      visible: userProfile?.Features.View_KB && userProfile?.Features.KnowledgeBase ? 'true' : null
    },
    {
      badge: kbResults?.length > 0 ? kbResults.length : null,
      title: "Knowlege base",
      text: "KBase",
      route: "/ticket/new/kbase",
      selected: isSelected('kbase'),
      image: "./images/lifesaver.png",
      visible: userProfile?.Features.View_KB && userProfile?.Features.KnowledgeBase ? 'true' : null
    },
    {
      separator: true,
      visible: 'true'
    },
    {
      badge: splitFromTicketOptions?.Ids?.length > 0 ? splitFromTicketOptions?.Ids?.length : null,
      title: "Split " + userProfile?.Phrases.Action + "s",
      text: "Split " + userProfile?.Phrases.Action + "s",
      route: "/ticket/new/split",
      selected: isSelected('split'),
      icon: "k-i-connector",
      // image: "./images/split-gray.png",
      visible: splitFromTicketOptions && splitFromTicketOptions.TicketId && splitFromTicketOptions.Ids?.length > 0
    },
    {
      separator: true,
      visible: 'true'
    },
  ];
  const onSelect = (e: DrawerSelectEvent) => {
    navigate(e.itemTarget.props.route);
  };
  const RenderItem = (props: DrawerItemProps) => {
    if (!props.visible) return <></>;
    return (
      <DrawerItem {...props} title={props.text}>
        {props.badge && !props.disabled ?
          <BadgeContainer>
            <div title={props.title}>
              {props.image
                ? <img alt="Tool" src={props.image} style={{ backgroundColor: userProfile?.Nav_Hex_Color, maxHeight: 18, borderRadius: 10 }} />
                : <span className={"k-font-icon " + props.icon} style={{ color: userProfile?.Nav_Hex_Color }} />
              }
              <Badge className='ml-1' size='small' align={{ horizontal: "end", vertical: "top" }}>{props.badge}</Badge>
            </div>
          </BadgeContainer>
          :
          <>
            {props.image
              ? <img alt="Tool" src={props.image} style={{ backgroundColor: userProfile?.Nav_Hex_Color, maxHeight: 18, borderRadius: 10 }} />
              : <span className={"k-font-icon " + props.icon} style={{ color: userProfile?.Nav_Hex_Color }} />
            }

          </>
        }
        <div className="item-descr-wrap">
          <div>{props.text}</div>
        </div>
      </DrawerItem>
    );
  };

  useEffect(() => {
    //Set the other template details like description, type, status etc.
    console.log('Template useEffect', template, status)
    if (template) {
      //if (!ticket.Template_Populated) {
      //Call description
      template.Call_Desc && dispatch(TicketSlice.SetDescription(decodeURIComponent(template.Call_Desc)))

      //Call Type
      template.Sub_Type_Id && dispatch(TicketSlice.SetType(template.Sub_Type_Id))
      // }

      //Call Status
      template.Status_Id && status && dispatch(TicketSlice.SetStatus(status))

      //Call Assignment
      if (template.Assignment?.length) {
        let newAssigned = template.Assignment.map(a => { return { ID: a.Contact_Id, Full_Name: a.Full_Name } as IStaff })
        dispatch(TicketSlice.SetAssigned(newAssigned))
      }


      //dispatch(TicketSlice.SetTemplatePopulated(true))
    }
  }, [template]);

  useEffect(() => {
    console.log('Template Status useEffect')
    //Call Status
    if (template?.Status_Id && status) {
      dispatch(TicketSlice.SetStatus(status))
      console.log('Template useEffect: Status', template, status)

    }

  }, [template, status]);

  //KB Search When ticket description changes
  const [searchKB] = KBApi.useSearchKBMutation();
  const [wordCount, setWordCount] = useState(0)
  useEffect(() => {
    //console.log('KnowledgeBase - desc changed');
    const fetchResults = async () => {
      let currentWordCount = (ticket?.Call_Desc).split(" ").length;
      //Search
      if (!ticket?.Call_Desc) {
        dispatch(KBSlice.SetResults([]))
      } else if (currentWordCount > 0 && wordCount !== currentWordCount) {
        var kbResults = await searchKB({ Description: ticket.Call_Desc, EndUserId: userProfile.Id } as KBSearchProps).unwrap()

        //Update results
        dispatch(KBSlice.SetResults(kbResults as IKBSearchResult[]))

        setWordCount(currentWordCount);
      }
    }
    fetchResults();
  }, [ticket.Call_Desc])

  const requiredStyleClassV25 = `    
  .k-window.ticket-details .k-window-titlebar{
  background-color: ${userProfile?.Nav_Hex_Color};
  color:#fff;`

  return (
    <>
      <style id='required-section-styles'>
        {requiredStyleClassV25}
      </style>
      <Drawer
        expanded={props.expanded}
        position="start"
        mode="push"
        mini={true}
        width={175}
        items={items}
        item={RenderItem}
        onSelect={onSelect}
      >
        <DrawerContent>
          <Routes>
            {(userProfile?.Features.Call_Templates || userProfile?.Features.Workflow) && <Route path="templates" element={<Templates />} />}
            <Route path="details" element={<TicketDetails ticketSaved={props.ticketSaved} />} />
            <Route path="existing" element={<ExistingTickets {...ticket} Is_Loading={false} />} />
            <Route path="hierarchy" element={<TicketHierarchy />} />
            <Route path="assets" element={<TicketAssets />} />
            <Route path="file" element={<Attachments />} />
            <Route path="kbase" element={<KnowledgeBase />} />
            <Route path="split" element={<SplitActions />} />
            <Route path="*" element={<TicketDetails ticketSaved={props.ticketSaved} />} />
            {/* <Route path="*" element={<Navigate to={{pathname:'details',search:{templateId:tem}}} />} /> */}
          </Routes>
        </DrawerContent>
      </Drawer>
      <WindowActionsBar>
        <div className="flex m-2">
          <div className='flex w-full'>
            <LogOptions />
          </div>
          <div className='inline-block ml-1'>
            <Button onClick={() => { navigate('/ticket/new/details') }} form='ticket-form' type={"submit"} style={{ background: userProfile?.Nav_Hex_Color }} className='log-button'>
              <img src='./images/Log-Ticket-White.png' className='inline mr-2 w-5' alt="Log Ticket" />
              Save {userProfile?.Phrases.CallPhrase}
            </Button>
            {/* <Button onClick={() => {
              dispatch(TicketSlice.ResetTicket(null));
            }} >Reset!</Button> */}
          </div>
        </div>
      </WindowActionsBar>
    </>
  )
}

export default TicketDrawer;